import React from "react"
import PropTypes from "prop-types"

/**
 * Resolves an arbitrary lecture URL into something renderable.
 *
 * Providers that forbid framing in a <video> tag (YouTube, Vimeo) are turned
 * into their embed URL and rendered in an iframe; anything else (a direct MP4
 * from the school's own backend, an HLS manifest, a CDN link) is played with
 * the native element. Adding a provider means adding one entry here.
 */
export const resolveVideoSource = (rawUrl) => {
    const url = (rawUrl || "").trim()
    if (!url) return { kind: "none", src: "" }

    const youtube = url.match(
        /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/
    )
    if (youtube) {
        return {
            kind: "embed",
            src: `https://www.youtube.com/embed/${youtube[1]}?rel=0&modestbranding=1`
        }
    }

    const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/)
    if (vimeo) {
        return {
            kind: "embed",
            src: `https://player.vimeo.com/video/${vimeo[1]}`
        }
    }

    return { kind: "file", src: url }
}

/**
 * Which host a lecture URL plays from, and a still image for it when the
 * host serves one without an API call (YouTube does; Vimeo does not).
 */
export const describeVideoUrl = (rawUrl) => {
    const url = (rawUrl || "").trim()
    const youtube = url.match(
        /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/
    )
    if (youtube) {
        return {
            provider: "YouTube",
            thumbnail: `https://i.ytimg.com/vi/${youtube[1]}/mqdefault.jpg`
        }
    }
    if (/vimeo\.com\/(?:video\/)?\d+/.test(url)) {
        return { provider: "Vimeo", thumbnail: null }
    }
    return { provider: url ? "Video file" : null, thumbnail: null }
}

export default function VideoPlayer({ url, title, poster }) {
    const { kind, src } = React.useMemo(
        () => resolveVideoSource(url),
        [url]
    )

    if (kind === "none") {
        return (
            <div className="flex aspect-video w-full items-center justify-center rounded-2xl bg-slate-900 px-6 text-center text-sm text-slate-300">
                This lecture has no video attached yet.
            </div>
        )
    }

    return (
        <div className="relative w-full overflow-hidden rounded-2xl bg-black shadow-lg">
            {/* aspect-video keeps the frame 16:9 at every breakpoint */}
            <div className="aspect-video w-full">
                {kind === "embed" ? (
                    <iframe
                        className="h-full w-full"
                        src={src}
                        title={title || "Lecture video"}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    />
                ) : (
                    <video
                        className="h-full w-full"
                        src={src}
                        poster={poster || undefined}
                        controls
                        controlsList="nodownload"
                        preload="metadata"
                    >
                        Your browser does not support embedded video.
                    </video>
                )}
            </div>
        </div>
    )
}

VideoPlayer.propTypes = {
    url: PropTypes.string,
    title: PropTypes.string,
    poster: PropTypes.string
}
